/* eslint-disable no-unused-vars, no-extra-semi, no-prototype-builtins */
import moment from 'moment'
import React, { useState, useEffect, useCallback } from 'react'
import { connect, useSelector } from 'react-redux'

import { isObjectEmpty } from 'helpers/object'
import useVideoContext from 'hooks/useVideoContext/useVideoContext'
import { startQuiz } from 'services/apis/quiz'
import { resetGeneralMessages } from 'store/actions/WebSocket'
import { dispatchSendSocketMessage } from 'store/dispatcher'
import { _isStudent, _isTeacher, _userInfo } from 'store/selectors/User'
import * as thunks from 'store/thunks/Quiz'
import { logger } from 'utils/logger'

import { quizKeys } from './constants'
import EndQuizScreen from './EndQuizScreen'
import { QuestionTracker, WaitingToResume } from './MiscQuizInfo'
import { NextQuestionButton, SkipQuestionButton } from './MotionButton'
import QuestionsLayout from './QuestionsLayout'
import StartQuizScreen from './StartQuizScreen'
import { QuizViewWrapper, Container } from './styles'

const Quiz3 = props => {
    const { roomName, roomModeState } = useVideoContext()
    const [quiz, setQuiz] = useState()
    const [quizSet, setQuizSet] = useState()
    const [hasQuizStarted, setHasQuizStarted] = useState(false)
    const [question, setQuestion] = useState()
    const [choices, setChoices] = useState({})
    const [isMCQ, setIsMCQ] = useState(false)

    const [timer, setTimer] = useState(0)

    const [intervalId, setIntervalId] = useState(null)

    const isTeacher = useSelector(_isTeacher)
    const isStudent = useSelector(_isStudent)

    const [openEndScreen, setOpenEndScreen] = useState(false)

    /* Fetch all the quiz sets on component mount */
    useEffect(() => {
        if (isStudent || roomModeState === 'quiz') {
            props.onGetActiveQuestion(roomName)
        }
        return () => {
            reset()
            setHasQuizStarted(false)
        }
    }, [])

    useEffect(() => {
        if (!isObjectEmpty(props.question) && !isObjectEmpty(props.quiz)) {
            setQuiz(props.quiz)
            setQuestion(props.question)
            setHasQuizStarted(true)
        }
    }, [props.quiz, props.question])

    const onProceed = async () => {
        try {
            const { data } = await startQuiz(roomName, quizSet.uuid)

            if (data.error) {
                /* TO-DO - SHOW ERROR TOAST */
                return
            }

            setHasQuizStarted(true)
            localStorage.setItem('quiz', 1)
        } catch (error) {
            const logErrorMsg = {
                msg: `[QUIZ ${quizSet.uuid}] Failed to start quiz.`,
                error,
            }
            logger.error(logErrorMsg)
            /* TO-DO - SHOW ERROR TOAST */
        }
    }

    const onNext = useCallback(() => {
        dispatchSendSocketMessage({
            action: quizKeys.NEXT_QUESTION,
            quiz: quiz.uuid,
        })
    }, [quiz])

    useEffect(() => {
        if (!props.msg) return

        if (props.msg.action === quizKeys.QUIZ) {
            const m = props.msg.message

            const { quiz, total, current, active_question } = m
            const {
                uuid,
                question,
                type,
                topic,
                choices,
                timer,
                question_end_time,
                answers,
            } = active_question

            if (m.active_question) {
                setOpenEndScreen(false)
                setHasQuizStarted(true)
                setQuiz({ uuid: quiz, total, current })
                setQuestion({
                    uuid,
                    question,
                    type,
                    topic,
                    choices,
                    timer,
                    question_end_time,
                    answers,
                })
                return
            }
        }

        // Answers
        if (props.msg.action === quizKeys.QUESTION_RESULT) {
            const m = props.msg.message

            if (question && quiz) {
                if (isMCQ) {
                    const temp = choices

                    for (const key in choices) {
                        temp[key].users = []
                    }

                    m.answers.forEach(ans => {
                        if (temp[ans.choice]) {
                            temp[ans.choice].users.push(ans.student_name)
                        }
                    })

                    setChoices({ ...temp })

                    return
                }
            }
        }

        if (
            quiz &&
            question &&
            [quizKeys.QUIZ_COMPLETE, quizKeys.QUIZ_CANCEL].includes(
                props.msg.action
            )
        ) {
            reset()
            setQuestion()
            setQuizSet()
            setQuiz()
            setHasQuizStarted()
            setOpenEndScreen(true)
        }
    }, [props.msg])

    const reset = isQuestion => {
        setTimer(0)
        clearInterval(intervalId)
        setIntervalId(null)
        setChoices({})

        setIsMCQ()
        !isQuestion && props.onResetState()
        !isQuestion && props.resetGeneralMessages()
    }

    useEffect(() => {
        if (!question || !question.question) return
        /* Rest state for new question */
        reset(true)
        const mcq = question?.type?.toUpperCase() === 'MCQ'
        setIsMCQ(mcq)
        const temp = {} // for mcq answers

        if (mcq) {
            /* Setup choices lookup table */
            question.choices.forEach(c => {
                if (!temp[c.uuid]) {
                    temp[c.uuid] = {
                        ...c,
                        state: null,
                        selected: false,
                        users: [],
                    }
                }
            })
        }

        if (question.answers && question.answers.length) {
            question.answers.forEach(ans => {
                if (isTeacher) {
                    if (mcq) {
                        temp[ans.choice].users.push(ans.student_name)
                    }
                }

                if (isStudent) {
                    if (mcq) {
                        temp[ans.choice].users.push(ans.student_name)
                    }
                }
            })
        }

        setChoices({ ...temp })

        if (question.question_end_time) {
            updateTimer(question)
        }
        /* Start Timer */
        startTimer()
    }, [question])

    const updateTimer = () => {
        const quesEndTime = moment(question.question_end_time) // 12:55:45
        const now = moment() // 12:55:10
        const diff = quesEndTime.diff(now, 'seconds') //35

        if (diff > 0) {
            // still time left for question to complete
            setTimer(question.timer - diff)
        } else {
            // times up!!
            setTimer(question.timer)
        }
    }

    const startTimer = () => {
        const id = setInterval(() => {
            setTimer(t => t + 1)
        }, 1000)
        setIntervalId(id)
    }

    /* Stop Timer */
    /* Clear Interval when timer is over */
    /* Update choices with Success/Fail results */
    useEffect(() => {
        if (!question) return

        if (timer >= question.timer) {
            clearInterval(intervalId)
            setIntervalId(null)
        }
    }, [timer])

    React.useEffect(() => {
        setQuizSet(props.quizSet)
    }, [props.quizSet])

    const isTimerOver = timer === question?.timer
    const showQuizButtons =
        question && hasQuizStarted && isTeacher && !openEndScreen
    const showNextButton = showQuizButtons && isTimerOver
    const showStartButton = !question && !hasQuizStarted && quizSet
    const showQuestionsLayout = question && hasQuizStarted && !openEndScreen
    const showSkipButton = showQuizButtons && !isTimerOver
    const isWaiting = isStudent && isTimerOver

    return (
        <Container>
            {showNextButton && <NextQuestionButton onClick={onNext} />}
            {showSkipButton && <SkipQuestionButton onClick={onNext} />}
            {hasQuizStarted && (
                <QuestionTracker
                    currentQues={quiz?.current}
                    totalQues={quiz?.total}
                    isTimerOver={isTimerOver}
                />
            )}
            {isWaiting && <WaitingToResume isTimerOver={isTimerOver} />}
            <QuizViewWrapper>
                {showStartButton && (
                    <StartQuizScreen quizSet={quizSet} onProceed={onProceed} />
                )}
                {showQuestionsLayout && (
                    <QuestionsLayout
                        question={question}
                        choices={choices}
                        timer={timer}
                        isTimerOver={isTimerOver}
                    />
                )}
                {openEndScreen && <EndQuizScreen />}
            </QuizViewWrapper>
        </Container>
    )
}

const mapStateToProps = state => {
    return {
        quiz: state.quiz.quiz,
        quizSet: state.quiz.quizSet,
        question: state.quiz.question,
        msg: state.websocket.msg,
    }
}

const mapDispatchToProps = dispatch => {
    return {
        onGetActiveQuestion: roomName =>
            dispatch(thunks.getActiveQuestion(roomName)),
        onResetState: () => dispatch(thunks.resetState()),
        resetGeneralMessages: () => dispatch(resetGeneralMessages()),
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(Quiz3)
